import { useImmer } from "use-immer";
import { useEffect, useState } from "react";
import ApiConnector from "../connector/ApiConnector";
import moment from "moment";
import UtilService from "../service/UtilService";
import PromoteWeightsModal from "../component/backtesterPage/PromoteWeightsModal";
import ConfirmModal from "../component/backtesterPage/ConfirmModal";

const TRIGGER_TYPE_LABELS = {
  NIGHTLY: "Nightly (scheduled)",
  EVENT_DRIVEN: "Event-driven (config change)",
  MANUAL: "Manual",
};

const CLASSIFICATION_LABELS = {
  TRUE_POSITIVE: "TP",
  FALSE_POSITIVE: "FP",
  TRUE_NEGATIVE: "TN",
  FALSE_NEGATIVE: "FN",
};

const DEFAULT_CLASSIFICATIONS = ["TRUE_POSITIVE", "FALSE_POSITIVE"];
const TX_PAGE_SIZE = 50;

const Component = () => {
  const [weights, updateWeights] = useImmer({});
  const [baselineWeights, updateBaselineWeights] = useImmer({});
  const [datasetInfo, updateDatasetInfo] = useImmer(null);
  const [result, updateResult] = useImmer(null);
  const [loading, setLoading] = useState(false);
  const [sourceConfig, updateSourceConfig] = useImmer(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  const [promoted, setPromoted] = useState(false);
  const [transactions, updateTransactions] = useImmer(null);
  const [txFilters, updateTxFilters] = useImmer({
    pan: "",
    transactionUid: "",
    merchantCode: "",
    countryCode: "",
    classifications: [...DEFAULT_CLASSIFICATIONS],
  });
  const [txPage, setTxPage] = useState(0);
  const [txLoading, setTxLoading] = useState(false);

  const loadInitial = async () => {
    const config = await ApiConnector.getLatestConfig();
    const info = await ApiConnector.getBacktesterDatasetInfo();
    const liveWeights =
      (config && config.riskEvaluation && config.riskEvaluation.weightConfig) ||
      {};
    updateWeights(() => structuredClone(liveWeights));
    updateBaselineWeights(() => structuredClone(liveWeights));
    updateSourceConfig(() => structuredClone(config));
    updateDatasetInfo(() => info);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const config = await ApiConnector.getLatestConfig();
      const info = await ApiConnector.getBacktesterDatasetInfo();
      if (!mounted) return;
      const liveWeights =
        (config &&
          config.riskEvaluation &&
          config.riskEvaluation.weightConfig) ||
        {};
      updateWeights(() => structuredClone(liveWeights));
      updateBaselineWeights(() => structuredClone(liveWeights));
      updateSourceConfig(() => structuredClone(config));
      updateDatasetInfo(() => info);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const cacheReady = datasetInfo && datasetInfo.cacheReady;

  const handleWeightChange = (key, value) => {
    updateWeights((d) => {
      d[key] = value === "" ? 0 : parseFloat(value);
    });
  };

  const resetWeights = () => {
    updateWeights(() => structuredClone(baselineWeights));
    updateResult(() => null);
  };

  const runEvaluate = async () => {
    setLoading(true);
    try {
      const res = await ApiConnector.evaluateBacktester(weights);
      updateResult(() => res);
      await loadTransactions(0);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (page) => {
    setTxLoading(true);
    try {
      const filters = {
        pan: txFilters.pan || null,
        transactionUid: txFilters.transactionUid
          ? Number(txFilters.transactionUid)
          : null,
        merchantCode: txFilters.merchantCode || null,
        countryCode: txFilters.countryCode || null,
        classifications: txFilters.classifications.length
          ? txFilters.classifications
          : null,
      };
      const res = await ApiConnector.getBacktesterTransactionDetails(weights, filters, {
        page,
        pageSize: TX_PAGE_SIZE,
      });
      updateTransactions(() => res);
      setTxPage(page);
    } finally {
      setTxLoading(false);
    }
  };

  const handleTxFilterChange = (key, value) => {
    updateTxFilters((d) => {
      d[key] = value;
    });
  };

  const toggleClassification = (value) => {
    updateTxFilters((d) => {
      const idx = d.classifications.indexOf(value);
      if (idx >= 0) d.classifications.splice(idx, 1);
      else d.classifications.push(value);
    });
  };

  // Auto-reload transactions (debounced) whenever a filter changes,
  // but only after the first evaluation produced a transaction list.
  useEffect(() => {
    if (!transactions) return;
    const handle = setTimeout(() => loadTransactions(0), 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    txFilters.pan,
    txFilters.transactionUid,
    txFilters.merchantCode,
    txFilters.countryCode,
    txFilters.classifications,
  ]);

  const totalTxPages = () =>
    transactions
      ? Math.max(1, Math.ceil(transactions.totalMatching / transactions.pageSize))
      : 1;

  const ruleHeaderLabel = (header) =>
    header.replace(/_rule_score$/, "").replace(/_/g, " ");

  const changedEntries = () =>
    Object.keys(weights)
      .filter((key) => !UtilService.equalish(weights[key], baselineWeights[key]))
      .map((key) => ({
        key,
        current: baselineWeights[key],
        next: weights[key],
      }));

  const openPromote = () => {
    if (!sourceConfig || changedEntries().length === 0) return;
    setPromoteOpen(true);
  };

  const confirmPromote = async () => {
    if (!sourceConfig) return;
    setPromoting(true);
    try {
      // Out-of-sync check without the extra window.confirm from ConfigService:
      // this modal is the single confirmation for the promote action.
      const latest = await ApiConnector.getLatestConfig();
      if (latest && latest.id !== sourceConfig.id) {
        const ok = window.confirm(
          "The live config changed since you opened the Backtester. " +
            "Promoting will overwrite those changes. Continue?",
        );
        if (!ok) return;
      }

      const changed = changedEntries();
      const newConfig = structuredClone(sourceConfig);
      const re = newConfig.riskEvaluation;
      re.weightConfig = { ...re.weightConfig };
      re.weightManualConfig = { ...(re.weightManualConfig || {}) };
      for (const { key, next } of changed) {
        re.weightConfig[key] = next;
        re.weightManualConfig[key] = next; // mark manually set
      }

      const saved = await ApiConnector.createConfig(newConfig);
      if (!saved) return;

      // Refresh from the backend so the promoted config becomes the new baseline.
      await loadInitial();
      updateResult(() => null);
      setPromoteOpen(false);
      setPromoted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.setTimeout(() => setPromoted(false), 10000);
    } finally {
      setPromoting(false);
    }
  };

  const confirmRebuild = async () => {
    setRebuildOpen(false);
    await runRebuild();
  };

  const runRebuild = async () => {
    const info = await ApiConnector.rebuildBacktester();
    updateDatasetInfo(() => info);
    // The rebuild job runs asynchronously on the backend, so poll dataset-info
    // until the cache is ready (or a few attempts pass) to unlock the UI.
    for (let attempt = 0; attempt < 5; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const latest = await ApiConnector.getBacktesterDatasetInfo();
      updateDatasetInfo(() => latest);
      if (!latest.rebuildInProgress && latest.status === "READY") {
        break;
      }
    }
  };

  const formatTimestamp = (value) =>
    value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "—";

  const formatPercentChange = (baseline, simulated) => {
    if (!baseline) return "—";
    const pct = ((simulated - baseline) / baseline) * 100;
    const sign = pct > 0 ? "+" : "";
    return sign + pct.toFixed(1) + " %";
  };

  const formatDelta = (delta) => (delta > 0 ? "+" + delta : "" + delta);

  // For True Positives a higher value is better; for False Positives a lower
  // value is better. Colour each column independently accordingly.
  const higherIsBetterClass = (delta) =>
    delta === 0 ? "" : delta > 0 ? "good" : "warn";
  const lowerIsBetterClass = (delta) =>
    delta === 0 ? "" : delta < 0 ? "good" : "warn";

  const renderDeltaValue = (delta) =>
    delta === 0 ? formatDelta(delta) : <strong>{formatDelta(delta)}</strong>;

  const renderResultTable = (
    tpLabel,
    fpLabel,
    baselineTp,
    baselineFp,
    simulatedTp,
    simulatedFp,
    tpDelta,
    fpDelta,
  ) => (
    <table className="config-table-double">
      <thead>
        <tr>
          <th></th>
          <th>{tpLabel}</th>
          <th>{fpLabel}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Baseline</td>
          <td>{baselineTp}</td>
          <td>{baselineFp}</td>
        </tr>
        <tr>
          <td>Simulated</td>
          <td>{simulatedTp}</td>
          <td>{simulatedFp}</td>
        </tr>
        <tr>
          <td>Delta</td>
          <td className={higherIsBetterClass(tpDelta)}>
            {renderDeltaValue(tpDelta)}
          </td>
          <td className={lowerIsBetterClass(fpDelta)}>
            {renderDeltaValue(fpDelta)}
          </td>
        </tr>
        <tr>
          <td>Change</td>
          <td className={higherIsBetterClass(tpDelta)}>
            {formatPercentChange(baselineTp, simulatedTp)}
          </td>
          <td className={lowerIsBetterClass(fpDelta)}>
            {formatPercentChange(baselineFp, simulatedFp)}
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <>
      <header className="subheader">
        <h1 className="header-title">Backtester</h1>
      </header>
      <main>
        {promoted && (
          <div
            className="info-box"
            style={{ borderColor: "#2e7d32", color: "#2e7d32" }}
          >
            <p>
              <strong>Live config updated.</strong> The promoted weights are now
              the live configuration and the new baseline.
            </p>
          </div>
        )}
        <div className="info-box">
          <p>
            <strong>This is a read-only simulation.</strong> Editing weights and
            running an evaluation here does <strong>not</strong> change the live
            Rule configuration. It only replays the pre-loaded 30-day dataset
            against your proposed weights to preview the Effective True/False
            Positive impact. When you are happy with the result, use{" "}
            <strong>Promote to live config</strong> to create a new live
            configuration from the weights you changed (they are saved as
            manually set weights).
          </p>
        </div>

        <h2>Dataset</h2>
        {datasetInfo === null ? (
          <p className="gray">Loading dataset info…</p>
        ) : (
          <table className="config-table">
            <tbody>
              <tr>
                <td>Status</td>
                <td>
                  {datasetInfo.status === "EMPTY" ? (
                    <span className="warn">
                      No transactions in the last 30 days — nothing to evaluate
                    </span>
                  ) : datasetInfo.status === "READY" ? (
                    <span>Ready</span>
                  ) : (
                    <span className="gray">
                      Dataset not built yet — building now…
                    </span>
                  )}
                  {datasetInfo.rebuildInProgress && (
                    <span className="warn"> (rebuild in progress)</span>
                  )}
                </td>
              </tr>
              <tr>
                <td>Generated at</td>
                <td>{formatTimestamp(datasetInfo.generatedAt)}</td>
              </tr>
              <tr>
                <td>Data range</td>
                <td>
                  {formatTimestamp(datasetInfo.trnDateMin)} —{" "}
                  {formatTimestamp(datasetInfo.trnDateMax)}
                </td>
              </tr>
              <tr>
                <td>Transactions in dataset</td>
                <td>{datasetInfo.rowCount}</td>
              </tr>
              <tr>
                <td>Frauds in dataset</td>
                <td>{datasetInfo.fraudCount}</td>
              </tr>
              <tr>
                <td>Last trigger</td>
                <td>
                  {TRIGGER_TYPE_LABELS[datasetInfo.triggerType] ||
                    datasetInfo.triggerType ||
                    "—"}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <h2>Rule weights</h2>
        <table className="config-table">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Current weights</th>
              <th>New weights</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(weights).map(([key, value]) => (
              <tr key={key}>
                <td>{UtilService.camelCaseToReadable(key)}</td>
                <td className="gray">{baselineWeights[key]}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={value}
                    onChange={(e) => handleWeightChange(key, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="row">
          <div className="col">
            <button
              className="nav-button"
              onClick={runEvaluate}
              disabled={loading || !cacheReady}
              title={cacheReady ? "Run simulation" : "Dataset is not ready yet"}
            >
              {loading ? "Evaluating…" : "Evaluate"}
            </button>
            <button
              className="nav-button"
              onClick={resetWeights}
              disabled={loading}
            >
              Reset weights
            </button>
            <button
              className="nav-button"
              onClick={() => setRebuildOpen(true)}
              disabled={datasetInfo !== null && datasetInfo.rebuildInProgress}
              title="Rebuild the 30-day dataset from the latest transactions"
            >
              Rebuild dataset now
            </button>
            <button
              className="nav-button"
              onClick={openPromote}
              disabled={
                loading || !sourceConfig || changedEntries().length === 0
              }
              title="Create a new live config from the changed weights"
            >
              ⚠ Promote to live config
            </button>
          </div>
        </div>

        {result && (
          <>
            <h2>Result</h2>
            {renderResultTable(
              "True Positives",
              "False Positives",
              result.baselineTruePositives,
              result.baselineFalsePositives,
              result.simulatedTruePositives,
              result.simulatedFalsePositives,
              result.truePositivesDelta,
              result.falsePositivesDelta,
            )}
            {renderResultTable(
              "Effective True Positives",
              "Effective False Positives",
              result.baselineEffectiveTruePositives,
              result.baselineEffectiveFalsePositives,
              result.simulatedEffectiveTruePositives,
              result.simulatedEffectiveFalsePositives,
              result.effectiveTruePositivesDelta,
              result.effectiveFalsePositivesDelta,
            )}
            <p className="gray">
              <strong>Effective</strong> counts exclude transactions another WAG
              system would have stopped anyway (reject codes other than
              pB/cB/2), matching the Success-rate dashboard. Plain counts
              include every predicted-positive transaction.
            </p>
            <h3>Confusion matrix</h3>
            <table className="config-table">
              <thead>
                <tr>
                  <th></th>
                  <th>True Positives</th>
                  <th>False Positives</th>
                  <th>True Negatives</th>
                  <th>False Negatives</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Baseline</td>
                  <td>{result.baselineTruePositives}</td>
                  <td>{result.baselineFalsePositives}</td>
                  <td>{result.baselineTrueNegatives}</td>
                  <td>{result.baselineFalseNegatives}</td>
                </tr>
                <tr>
                  <td>Simulated</td>
                  <td>{result.simulatedTruePositives}</td>
                  <td>{result.simulatedFalsePositives}</td>
                  <td>{result.simulatedTrueNegatives}</td>
                  <td>{result.simulatedFalseNegatives}</td>
                </tr>
                <tr>
                  <td>Delta</td>
                  <td className={higherIsBetterClass(result.truePositivesDelta)}>
                    {renderDeltaValue(result.truePositivesDelta)}
                  </td>
                  <td className={lowerIsBetterClass(result.falsePositivesDelta)}>
                    {renderDeltaValue(result.falsePositivesDelta)}
                  </td>
                  <td className={higherIsBetterClass(result.trueNegativesDelta)}>
                    {renderDeltaValue(result.trueNegativesDelta)}
                  </td>
                  <td className={lowerIsBetterClass(result.falseNegativesDelta)}>
                    {renderDeltaValue(result.falseNegativesDelta)}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="gray">
              {result.transactionsConsidered} transactions (
              {result.fraudsConsidered} frauds) evaluated in{" "}
              {result.computeMillis} ms.
            </p>
          </>
        )}

        {transactions && (
          <>
            <h2>Transactions</h2>
            <p className="gray">
              Per-transaction breakdown of the simulated evaluation. Classes
              (TP/FP/TN/FN) are derived from the simulated weights; scores are
              the weighted risk. Filter by PAN, Transaction UID, Merchant ID or
              Country code.
            </p>
            <table className="config-table">
              <thead>
                <tr>
                  <th>Filter</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>PAN</td>
                  <td>
                    <input
                      type="text"
                      value={txFilters.pan}
                      onChange={(e) =>
                        handleTxFilterChange("pan", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>Transaction UID</td>
                  <td>
                    <input
                      type="text"
                      value={txFilters.transactionUid}
                      onChange={(e) =>
                        handleTxFilterChange("transactionUid", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>Merchant ID</td>
                  <td>
                    <input
                      type="text"
                      value={txFilters.merchantCode}
                      onChange={(e) =>
                        handleTxFilterChange("merchantCode", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>Country code</td>
                  <td>
                    <input
                      type="text"
                      value={txFilters.countryCode}
                      onChange={(e) =>
                        handleTxFilterChange("countryCode", e.target.value)
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>Classifications</td>
                  <td>
                    {Object.keys(CLASSIFICATION_LABELS).map((c) => (
                      <label key={c} style={{ marginRight: "12px" }}>
                        <input
                          type="checkbox"
                          checked={txFilters.classifications.includes(c)}
                          onChange={() => toggleClassification(c)}
                        />{" "}
                        {CLASSIFICATION_LABELS[c]}
                      </label>
                    ))}
                  </td>
                </tr>
              </tbody>
            </table>
            <table className="config-table-double table-scrollable">
                <thead>
                  <tr>
                    <th>UID</th>
                    <th>PAN</th>
                    <th>Merchant</th>
                    <th>Country</th>
                    <th>Is Fraud</th>
                    <th>WAG rejected</th>
                    <th>Baseline</th>
                    <th>Simulated</th>
                    <th>Baseline score</th>
                    <th>Simulated score</th>
                    {transactions.ruleHeaders.map((h) => (
                      <th key={h}>{ruleHeaderLabel(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.rows.length === 0 ? (
                    <tr>
                      <td colSpan={10 + transactions.ruleHeaders.length}>
                        <span className="gray">No matching transactions.</span>
                      </td>
                    </tr>
                  ) : (
                    transactions.rows.map((row) => (
                      <tr key={row.transactionUid}>
                        <td className="use-mono-font">{row.transactionUid}</td>
                        <td className="use-mono-font">{row.pan}</td>
                        <td className="use-mono-font">{row.merchantCode}</td>
                        <td className="use-mono-font">{row.countryCode}</td>
                        <td className="use-mono-font">
                          {row.fraud ? "Yes" : "No"}
                        </td>
                        <td className="use-mono-font">
                          {row.wagRejected ? "Yes" : "No"}
                        </td>
                        <td className="use-mono-font">
                          {CLASSIFICATION_LABELS[row.baselineClassification] ||
                            row.baselineClassification}
                        </td>
                        <td className="use-mono-font">
                          {CLASSIFICATION_LABELS[row.simulatedClassification] ||
                            row.simulatedClassification}
                        </td>
                        <td className="use-mono-font">
                          {row.baselineScore.toFixed(2)}
                        </td>
                        <td className="use-mono-font">
                          {row.simulatedScore.toFixed(2)}
                        </td>
                        {row.ruleScores.map((s, i) => (
                          <td className="use-mono-font" key={i}>
                            {s}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            <div className="row">
              <div className="col">
                <button
                  className="nav-button"
                  onClick={() => loadTransactions(txPage - 1)}
                  disabled={txLoading || txPage === 0}
                >
                  Previous
                </button>
                <span className="gray" style={{ margin: "0 12px" }}>
                  Page {txPage + 1} of {totalTxPages()} (
                  {transactions.totalMatching} matching)
                </span>
                <button
                  className="nav-button"
                  onClick={() => loadTransactions(txPage + 1)}
                  disabled={
                    txLoading ||
                    (txPage + 1) * transactions.pageSize >=
                      transactions.totalMatching
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {promoteOpen && (
        <PromoteWeightsModal
          changes={changedEntries()}
          onCancel={() => setPromoteOpen(false)}
          onConfirm={() => setPromoteConfirmOpen(true)}
          saving={promoting}
        />
      )}

      {rebuildOpen && (
        <ConfirmModal
          title="Rebuild dataset now"
          confirmLabel="Rebuild now"
          danger
          onCancel={() => setRebuildOpen(false)}
          onConfirm={confirmRebuild}
          message={
            <>
              <p>
                This recomputes the whole 30-day window from production data. On
                production it can take several minutes to finish.
              </p>
              <p>
                The current dataset stays available the entire time and switches
                over automatically once the new one is ready.
              </p>
            </>
          }
        />
      )}

      {promoteConfirmOpen && (
        <ConfirmModal
          title="Are you sure you want to promote this weight set?"
          confirmLabel="Promote"
          danger
          fitContent
          centerActions
          busy={promoting}
          onCancel={() => setPromoteConfirmOpen(false)}
          onConfirm={async () => {
            await confirmPromote();
            setPromoteConfirmOpen(false);
          }}
        />
      )}
    </>
  );
};

export default Component;
